import React, { useState, useEffect, useMemo } from 'react';

const SANS = "'Plus Jakarta Sans',sans-serif";
const MONO = "'IBM Plex Mono',monospace";
const today = () => new Date().toISOString().slice(0, 10);

// Create or edit a voyage. Handles the single writeup, dates, cover pick, and
// bulk date-range assignment of existing entries.
export default function VoyageModal({ isOpen, onClose, onSave, editVoyage, allEntries = [] }) {
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '', cover_url: '', writeup: '' });
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isOpen) return;
    if (editVoyage) {
      setForm({
        name: editVoyage.name || '',
        start_date: editVoyage.start_date || '',
        end_date: editVoyage.end_date || '',
        cover_url: editVoyage.cover_url || '',
        writeup: editVoyage.writeup || '',
      });
      // default the range to the voyage's own span
      setRangeStart(editVoyage.start_date || '');
      setRangeEnd(editVoyage.end_date || today());
    } else {
      setForm({ name: '', start_date: today(), end_date: '', cover_url: '', writeup: '' });
      setRangeStart(today());
      setRangeEnd(today());
    }
  }, [isOpen, editVoyage]);

  useEffect(() => {
    if (!isOpen) return;
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [isOpen, onClose]);

  // Entries that fall in the chosen range (for the bulk-assign preview).
  const rangeMatches = useMemo(() => {
    if (!rangeStart || !rangeEnd) return [];
    return allEntries.filter(e => e.date && e.date >= rangeStart && e.date <= rangeEnd);
  }, [allEntries, rangeStart, rangeEnd]);

  // Photos available to use as a cover (any photo on an entry already in this
  // voyage, or in the chosen range).
  const coverChoices = useMemo(() => {
    const pool = editVoyage
      ? allEntries.filter(e => e.voyage_id === editVoyage.id)
      : rangeMatches;
    const urls = [];
    pool.forEach(e => {
      if (e.photo_url) urls.push(e.photo_url);
      (Array.isArray(e.photos) ? e.photos : []).forEach(p => p?.url && urls.push(p.url));
    });
    return [...new Set(urls)].slice(0, 12);
  }, [editVoyage, allEntries, rangeMatches]);

  if (!isOpen) return null;

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        voyage: { ...form, id: editVoyage?.id },
        assignEntryIds: rangeMatches.map(e => e.id), // entries in range get this voyage
      });
      onClose();
    } catch { /* parent shows the error */ }
    setSaving(false);
  };

  const field = { width: '100%', fontFamily: SANS, fontSize: 15, border: '1px solid var(--border)', borderRadius: 10, padding: '11px 13px', background: '#fff', color: 'var(--ink)' };
  const lbl = { fontFamily: MONO, fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--slate)', display: 'block', marginBottom: 5 };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4300, background: 'rgba(11,22,38,.4)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 440, background: 'var(--bg)', borderRadius: 18, padding: 20, boxShadow: '0 18px 44px rgba(11,22,38,.24)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: SANS, fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>{editVoyage ? 'Edit voyage' : 'New voyage'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--slate)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ marginBottom: 12 }}><label style={lbl}>Name</label><input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="British Columbia 2026" style={field} /></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div><label style={lbl}>Start date</label><input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} style={field} /></div>
          <div><label style={lbl}>End date</label><input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} style={field} /></div>
        </div>
        <div style={{ fontFamily: SANS, fontSize: 11, color: 'var(--slate)', margin: '-4px 2px 14px' }}>Leave end date blank while the voyage is ongoing.</div>

        {/* Bulk assign by date range */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <label style={lbl}>Add entries in this date range</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} style={field} />
            <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} style={field} />
          </div>
          <div style={{ fontFamily: SANS, fontSize: 12, color: 'var(--slate)', marginTop: 8 }}>
            {rangeMatches.length} {rangeMatches.length === 1 ? 'entry' : 'entries'} in range will join this voyage.
          </div>
        </div>

        {/* Cover picker */}
        {coverChoices.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Cover photo</label>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {coverChoices.map(url => (
                <img key={url} src={url} alt="" onClick={() => set('cover_url', url)} style={{ flex: 'none', width: 76, height: 76, borderRadius: 10, objectFit: 'cover', cursor: 'pointer', border: form.cover_url === url ? '3px solid var(--gold)' : '3px solid transparent' }} />
              ))}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>The Voyage — writeup</label>
          <textarea value={form.writeup} onChange={e => set('writeup', e.target.value)} placeholder="Left Elliott Bay on the night of July 2…" style={{ ...field, minHeight: 120, resize: 'vertical', lineHeight: 1.6 }} />
        </div>

        <button onClick={save} disabled={saving || !form.name.trim()} style={{ width: '100%', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 12, padding: 15, fontFamily: SANS, fontSize: 14, fontWeight: 600, cursor: (saving || !form.name.trim()) ? 'default' : 'pointer', opacity: (saving || !form.name.trim()) ? .55 : 1 }}>
          {saving ? 'Saving…' : editVoyage ? `Save · ${rangeMatches.length} entries in range` : `Create voyage · adds ${rangeMatches.length} entries`}
        </button>
      </div>
    </div>
  );
}
